import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output} from "@angular/core";
import {Subject} from "rxjs";
import {distinctUntilChanged, filter, first, switchMap, takeUntil} from "rxjs/operators";
import {UserSubscription} from "../../../../../user/user-subscription";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {ActivatedRoute, Router} from "@angular/router";
import {Website} from "../../../website";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {WebPViaServerDialogComponent} from "../../dialogs/webp-via-server-dialog/webp-via-server-dialog.component";
import {DbModalService} from "../../../../../modules/modal/modal";
import {MigrationService} from "../../services/migration.service";
import {PerformanceService} from "../../../performance/performance.service";
import {OptimizeStatus} from "../../mappers/optimize-status";
import {CacheStatus} from "../../mappers/cache-status";
import {ActivityAction} from "../../../../../activities/activity";
import {Product} from "../../../product";
import {BuilderSectionsInteractionService} from "../../../../../builder-v2/builder-sections-interaction.service";


@Component({
    selector: 'db-booster-notices',
    templateUrl: './notification-notices.component.html',
    styleUrls: ['./notification-notices.component.scss'],
    standalone: false
})
export class NotificationNoticesComponent implements OnInit, OnChanges, OnDestroy {
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public userSubscription: UserSubscription;
  public website: Website;
  public conflictingPluginsList: any;
  public webpStatus: any;
  public hasCacheStatus: boolean;
  public hasOptimizeStatus: boolean;
  public cacheStatus: CacheStatus;
  public optimizeStatus: OptimizeStatus;
  public cacheEnabled: boolean = true;
  public optimizerPlugin: Product;
  public companyName: string = 'Dashboard';
  @Input() websiteId: number;
  @Input() cachingStatus?: boolean;
  @Input() disableOptions?: boolean;
  @Output() onClickAction?: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _dbModal: DbModalService,
    private _performanceService: PerformanceService,
    private _migrationService: MigrationService,
    private _store: Store<fromStore.DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _builderSectionsInteractionService: BuilderSectionsInteractionService
  ) {}

  ngOnInit() {
    this._getUser();
    this._getWebsite();
    this._getBoosterPlugin();
    this._getBoosterData();
    this._getOptimizeStatus();
    this._getCompanyInfo();
  }

  ngOnChanges() {
    this.cacheEnabled = !!this.cachingStatus;
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _getUser(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserSubscription)),
        first()
      )
      .subscribe((userSubscription: UserSubscription) => {
        this.userSubscription = userSubscription;
      });
  }

  private _getWebsite(): void {
    this._store.select(fromStore.getWebsitesLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => this._store.select(fromStore.getWebsite(this.websiteId))),
        first(website => !!website)
      )
      .subscribe(website => {
        this.website = website;
      });
  }

  public reanalyze(): void {
    this._performanceService.getGooglePageSpeedScore(this.websiteId);
  }

  private _getBoosterIncompatibleActivePlugins(): void {
    this._store.select(fromStore.getWebsiteBoosterIncompatibleActivePlugins(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(plugins => {
        if (!TypeUtils.isNull(plugins) && !TypeUtils.isEmpty(plugins)) {
          const pluginsList = Object.values(plugins);
          this.conflictingPluginsList = pluginsList.join(", ");
        }
      });
  }

  private _getBoosterWebpStatus(): void {
    this._store.select(fromStore.getWebsiteWebpStatus(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(webpStatus => {
        if (!TypeUtils.isNull(webpStatus)) {
          this.webpStatus = webpStatus;
        }
      });
  }

  private _getBoosterCacheStatus(): void {
    this._store.select(fromStore.getWebsiteCacheStatus(this.websiteId))
      .pipe(
        distinctUntilChanged((oldData: CacheStatus, newData: CacheStatus) => oldData && newData && (oldData.cacheDate === newData.cacheDate && oldData.cacheEnabled === newData.cacheEnabled) ),
        takeUntil(this._destroyStream$)
      )
      .subscribe(cacheStatus => {
        if (!TypeUtils.isNull(cacheStatus)) {
          this.hasCacheStatus = true;
          this.cacheStatus = cacheStatus;
        }
      });
  }

  private _getBoosterData(): void {
    this._getBoosterIncompatibleActivePlugins();
    this._getBoosterWebpStatus();
    this._getBoosterCacheStatus();
  }

  private _getOptimizeStatus(): void {
    this._store.select(fromStore.getWebsiteBoosterOptimizeStatus({websiteId: this.websiteId, pageUrl: this.website.site_url}))
      .pipe(
        distinctUntilChanged((oldData: OptimizeStatus, newData: OptimizeStatus) => oldData && newData && (oldData.cachingDate === newData.cachingDate && oldData.pageIsCached === newData.pageIsCached) ),
        takeUntil(this._destroyStream$),
        filter(website => !!website)
      )
      .subscribe( optimizeStatus => {
        if (!TypeUtils.isNull(optimizeStatus)) {
          this.hasOptimizeStatus = true;
          this.optimizeStatus = optimizeStatus;
        }
      });
  }

  public closeWarning($event): void {
    $event.target.parentElement.parentElement.setAttribute('style', 'display: none;');
  }

  public webpInstructions(): void {
    this._router.navigate([`../image-optimizer`], {relativeTo: this._route});
    const modal = this._dbModal.open(WebPViaServerDialogComponent);
    modal.componentInstance.serverType = this.website.serverType;
  }

  public migrate(): void {
    if (this.userSubscription && this.userSubscription.trialBoosterClient) {
      const analyticsData = {
        eventCategory: 'Dashboard Action',
        eventAction: 'Upgrade button click',
        eventLabel: 'Speed Booster: Migrate your website from cache notice',
        websiteId: this.websiteId
      };
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
      this._router.navigate(['/upgrade-plan']);
    }
    else {
      this._migrationService.migrate(this.websiteId);
    }
  }

  public _getBoosterPlugin(): void {
    this._store.select(fromStore.getWebsiteOptimizerPlugin(this.websiteId))
      .subscribe((optimizerPlugin): void => {
        if (optimizerPlugin && !TypeUtils.isEmpty(optimizerPlugin)) {
          this.optimizerPlugin = optimizerPlugin;
        }
      });
  }

  public updateBoosterPlugin(): void {
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [this.optimizerPlugin],
      action: ActivityAction.UPDATE,
      network: 1
    }));
    this._btnClicked();
  }

  private _btnClicked() {
      this.onClickAction.emit();
  }

  private _getCompanyInfo(): void {
    this._builderSectionsInteractionService.companyInfo
      .pipe(
        filter(companyInfo => !!companyInfo),
        takeUntil(this._destroyStream$),
        first()
      )
      .subscribe(companyInfo => {
        this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
      });
  }

}
